import React, { useState, useRef, useMemo, lazy, useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { useDrag } from "@use-gesture/react";
// import common components
import SuspenseWrapper from "../../common-components/SuspenseWrapper";
import Typography, { TYPE_ELEMENTS } from "../../common-components/Typography";
// import HorizontalAccordionCards from "../../common-components/HorizontalAccordionCards";
// import CTACard from "../../common-components/cards/CTACard";
// import LinkCard from "../../common-components/cards/LinkCard";
import MoreInfoElement from "../../common-components/MoreInfoElement";
// import ProfileModal from "../../common-components/ProfileModal";
// import components
import VideoBannerLarge from "../../components/VideoBannerLarge";
// import Partners from "../../components/Partners";
// import common styles
import { NoPaddingPageContentWrapper } from "../../common-styles/PageContentWrapper";
import {
  HighlightSection,
  HighlightImg,
  HighlightSectionContent,
  HighlightItem,
  HighlightIcon,
} from "../../common-styles/HighlightSectionItems";
// import constants
import HOME_CONSTANTS from "../../utils/constants/homeConstants";
import ABOUT_CONSTANTS from "../../utils/constants/aboutUsConstants";
import CONSTANTS from "../../utils/constants/constants";
// import assets
import arrowLeft from "../../assets/icons/chevron-left.svg";

// lazy loading components
const HorizontalAccordionCards = lazy(() =>
  import("../../common-components/cards/HorizontalAccordionCards")
);
const CTACard = lazy(() => import("../../common-components/cards/CTACard"));
const LinkCard = lazy(() => import("../../common-components/cards/LinkCard"));
const ProfileModal = lazy(() => import("../../common-components/ProfileModal"));
const Partners = lazy(() => import("../../components/Partners"));

const FOUNDER = ABOUT_CONSTANTS.FOUNDERS.PEOPLE[0];

const {
  BANNER,
  USP,
  LARGE_LABEL,
  TECHNOLOGY,
  APPLICATIONS,
  MESSAGE,
  HIGHLIGHTS,
  PARTNERS,
} = HOME_CONSTANTS;

const HomeWrapper = styled.div`
  width: 100vw;
  max-width: 100%;
  height: fit-content;
  color: #fff;
  overflow: hidden;
  box-sizing: border-box;
`;

const Spacer = styled.div`
  width: 10vw;

  /* the subtractions from height are all based on nav height and have to be adjusted if the nav height changes */
  height: ${({ height }) => height};
`;

const LargeLabel = styled.div`
  position: relative;
  width: 100vw;
  box-sizing: border-box;
`;

const LargeLabelText = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 120px 272px;
  text-align: center;
  box-sizing: border-box;

  @media (max-width: 1280px) {
    padding: 96px 120px;
  }

  @media (max-width: 1024px) {
    padding: 80px 96px;
  }

  @media (max-width: 768px) {
    padding: 56px 64px;
  }

  @media (max-width: 640px) {
    padding: 40px 48px;
  }

  @media (max-width: 480px) {
    padding: 24px 32px;
  }
`;

const LargeLabelBg = styled.img`
  position: absolute;
  z-index: 0;
  bottom: 10px;
  left: 0px;
  width: 100vw;
  height: 100%;
  object-fit: cover;
  opacity: 1;
  -webkit-mask-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  mask-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0) 100%
  );

  @media (max-width: 840px) {
    -webkit-mask-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 1) 10%,
      rgba(0, 0, 0, 0) 90%
    );
    mask-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 1) 10%,
      rgba(0, 0, 0, 0) 90%
    );
  }
`;

const TechnologyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 52px;
  width: 100%;
  height: fit-content;
  padding: 96px 112px;
  color: #fff;
  box-sizing: border-box;
  text-align: center;

  @media (max-width: 1280px) {
    padding: 88px 96px;
    gap: 48px;
  }

  @media (max-width: 1024px) {
    padding: 72px 80px;
  }

  @media (max-width: 768px) {
    padding: 56px 64px;
    gap: 40px;
  }

  @media (max-width: 480px) {
    padding: 24px 32px;
    gap: 32px;
  }
`;

const TechsListWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const ResponsiveTechListWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 48px;
  box-sizing: border-box;
  justify-items: center;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr; /* stack vertically */
    gap: 40px;
  }
`;



const TechsListWindow = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow-x: scroll;

  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */

  /* Hide scrollbar for WebKit (Chrome, Safari, Opera) */
  &::-webkit-scrollbar {
    display: none;
  }
`;

const TechsList = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 52px;
  width: fit-content;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 1024px) {
    gap: 32px;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    gap: 40px;
  }

  @media (max-height: 480px) and (max-width: 1024px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
    flex-direction: column;
    gap: 40px;
  }
`;

const ArrowLeft = styled.img`
  width: 32px;
  height: 32px;
  cursor: pointer;

  @media (max-width: 900px) {
    display: none;
  }
`;

const ArrowRight = styled.img`
  width: 32px;
  height: 32px;
  cursor: pointer;
  transform: rotate(180deg);

  @media (max-width: 900px) {
    display: none;
  }
`;

const ApplicationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 52px;
  width: 100%;
  height: fit-content;
  padding: 96px 280px;
  color: #fff;
  box-sizing: border-box;
  text-align: center;

  @media (max-width: 1280px) {
    padding: 88px 96px;
    gap: 48px;
  }

  @media (max-width: 1024px) {
    padding: 72px 80px;
  }

  @media (max-width: 768px) {
    padding: 56px 64px;
    gap: 40px;
  }

  @media (max-width: 480px) {
    padding: 24px 32px;
    gap: 32px;
  }
`;

const BottomSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const MessageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  height: fit-content;
  padding: 96px 112px;
  color: #fff;
  box-sizing: border-box;
  text-align: center;

  @media (max-width: 1280px) {
    padding: 88px 96px;
    gap: 48px;
  }

  @media (max-width: 1024px) {
    padding: 72px 80px;
  }

  @media (max-width: 768px) {
    padding: 56px 64px;
    gap: 40px;
  }

  @media (max-width: 480px) {
    padding: 24px 32px;
    gap: 32px;
  }
`;

const MoreInfoWrapper = styled.div`
  position: relative;
  z-index: 1;
  width: fit-content;
  height: fit-content;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0px 8px;
  text-decoration: underline;
`;

const MessageBg = styled.img`
  position: absolute;
  z-index: 0;
  bottom: 0px;
  left: 0px;
  width: 100vw;
  height: 100%;
  object-fit: cover;

  height: 100%;
  object-fit: cover;

  -webkit-mask-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0) 32%
  );
  mask-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 0) 32%
  );

  @media (max-width: 840px) {
    -webkit-mask-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0) 48%
    );
    mask-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0) 48%
    );
  }
`;

const MessageVideo = styled.video`
  position: relative;
  z-index: 1;
  width: 40%;
  height: auto;
  object-fit: cover;
  transition: transform 0.4s ease, box-shadow 0.4s ease;

  @media (max-width: 1280px) {
    width: 50%;
  }

  @media (max-width: 1024px) {
    width: 60%;
  }

  @media (max-width: 768px) {
    width: 100%;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 10px grey;
  }
`;

const MessageIcons = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const HighlightWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 52px;
  width: 100%;
  padding: 96px 112px;
  color: #fff;
  box-sizing: border-box;
  text-align: center;

  @media (max-width: 1280px) {
    padding: 88px 96px;
    gap: 48px;
  }

  @media (max-width: 1024px) {
    padding: 72px 80px;
  }

  @media (max-width: 768px) {
    padding: 56px 64px;
    gap: 40px;
  }

  @media (max-width: 480px) {
    padding: 24px 32px;
    gap: 32px;
  }
`;

const HighlightHeader = styled.div`
  position: relative;
  z-index: 1;
  width: fit-content;
  height: fit-content;
`;

const HighlightBg = styled.img`
  position: absolute;
  z-index: 0;
  bottom: 0px;
  left: 0px;
  width: 100vw;
  height: 100%;
  object-fit: cover;

  -webkit-mask-image: linear-gradient(
    to top,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 1) 8%
  );
  mask-image: linear-gradient(to top, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 8%);

  @media (max-width: 840px) {
    -webkit-mask-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 1) 10%
    );
    mask-image: linear-gradient(
      to top,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 1) 10%
    );
  }
`;

const HighlightList = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 52px;
  width: 100%;
  flex-wrap: wrap;

  @media (max-width: 1280px),
    (max-width: 1400px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    row-gap: 48px;
    column-gap: 48px;
    width: 64%;

    .link-card-element {
      justify-self: center;
      width: 100%;
      height: fit-content;
    }
  }

  @media (max-width: 1024px) {
    // flex-direction: column;
    // align-items: center;
    width: 80%;
    gap: 40px;
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(1, 1fr);
  }

  @media (max-height: 768px) and (orientation: landscape) and (hover: none) and (pointer: coarse) {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 32px;
  }
`;

export default function Home() {
  const [spacerSize, setSpacerSize] = useState({
    width: "10vw",
    height: "100dvh",
  });
  const [showFounderModal, setShowFounderModal] = useState(false);
  const [showMssgVideo, setShowMssgVideo] = useState(false);
  const [isTechListOverflowing, setIsTechListOverflowing] = useState(false);

  const techsListRef = useRef(null);
  const techsListWindowRef = useRef(null);
  const techListArrowLeftRef = useRef(null);
  const techListArrowRightRef = useRef(null);
  const startScrollLeft = useRef(0);
  const contentWrapperRef = useRef(null);
  const highlightListRef = useRef(null);
  const mssgRef = useRef(null);

  useEffect(() => {
    const checkTechListOverflow = () => {
      setIsTechListOverflowing(
        techsListRef.current?.scrollWidth >
          techsListRef.current?.getBoundingClientRect().width
      );
    };

    checkTechListOverflow();

    window.addEventListener("resize", checkTechListOverflow);
    return () => window.removeEventListener("resize", checkTechListOverflow);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowMssgVideo(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 } // Trigger when 25% of video is visible
    );

    if (mssgRef.current) observer.observe(mssgRef.current);

    return () => observer.disconnect();
  }, []);

  useDrag(
    ({ movement: [mx], first }) => {
      if (first) {
        startScrollLeft.current = techsListRef.current.scrollLeft;
        techsListRef.current.style.cursor = "grabbing";
      }
      techsListRef.current.scrollLeft = startScrollLeft.current - mx;
    },
    {
      target: techsListRef,
      pointer: { buttons: 1 }, // Only enable for mouse, not for touch
    }
  );

  const scrollTechListWithArrow = (direction) => {
    if (direction === "left") {
      techsListWindowRef.current.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    } else {
      techsListWindowRef.current.scrollTo({
        left: techsListWindowRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  const deviceType = useMemo(() => {
    const ua = navigator.userAgent;

    if (/Mobi|Android/i.test(ua)) {
      // Likely a phone or tablet
      if (Math.min(window.screen.width, window.screen.height) <= 640) {
        return "phone";
      } else {
        return "tablet";
      }
    }

    return "desktop";
  }, []);

  return (
    <>
      <Helmet>
        <title>Sisir Radar | Home</title>
        <meta
          name="description"
          content="Our world's highest resolution L & P Band Synthetic Aperture Radar (SAR) delivers unmatched situational awareness in Earth Observation and Remote Sensing from spaceborne to airborne platforms—24/7, all-weather, anytime, anywhere."
        />
        <meta
          property="og:title"
          content="Sisir Radar | Home"
        />
        <meta
          property="og:description"
          content="Our world's highest resolution L & P Band Synthetic Aperture Radar (SAR) delivers unmatched situational awareness in Earth Observation and Remote Sensing from spaceborne to airborne platforms—24/7, all-weather, anytime, anywhere."
        />
        <meta property="og:image" content={BANNER.IMG} />
      </Helmet>
      <HomeWrapper className="home-wrapper">
        <VideoBannerLarge
          video={BANNER.VIDEO}
          img={BANNER.IMG}
          alt={BANNER.ALT}
          imgLandscape={BANNER.IMG_LANDSCAPE}
          headers={BANNER.HEADERS}
          subheader={BANNER.SUBHEADER}
          onSizeChange={setSpacerSize}
          onScrollClick={() => {
            contentWrapperRef.current.scrollIntoView({ behavior: "smooth" });
          }}
        />
        <Spacer
          className="spacer"
          width={spacerSize.width}
          height={"100dvh" /*spacerSize.height*/}
        />
        <NoPaddingPageContentWrapper ref={contentWrapperRef}>
          <HighlightSection className="highlight-section">
            <HighlightSectionContent>
              {USP.CONTENT.map((item, i) => {
                const { ICON, NAME } = item;
                return (
                  <HighlightItem key={i}>
                    <HighlightIcon
                      width="48px"
                      height="48px"
                      src={ICON}
                      alt={NAME}
                    />
                    <Typography
                      typeElement={TYPE_ELEMENTS.BODY_2}
                      customWeight="700"
                    >
                      {NAME}
                    </Typography>
                  </HighlightItem>
                );
              })}
            </HighlightSectionContent>
          </HighlightSection>
          <LargeLabel className="large-label">
            <LargeLabelText>
              <Typography
                typeElement={TYPE_ELEMENTS.H_2}
                customCase="uppercase"
              >
                {LARGE_LABEL.HEADER}
              </Typography>
            </LargeLabelText>
            <LargeLabelBg
              loading="lazy"
              className="large-label-bg"
              src={LARGE_LABEL.IMG}
              alt={LARGE_LABEL.ALT}
            />
          </LargeLabel>
          <TechnologyWrapper className="technology-wrapper">
            <Typography
              typeElement={TYPE_ELEMENTS.H_3}
              isOutline="true"
              customCase="uppercase"
            >
              {TECHNOLOGY.HEADER}
            </Typography>
            <ResponsiveTechListWrapper>
              {TECHNOLOGY.TECHS.map((tech, i) => {
                const { IMG, TITLE, TEXTS, LINK, ALT } = tech;
                return (
                  <SuspenseWrapper key={i}>
                    <CTACard
                      img={IMG}
                      alt={ALT}
                      title={TITLE}
                      texts={TEXTS}
                      ctaLabel={CONSTANTS.KNOW_MORE}
                      onClick={() => {
                        window.location.href = LINK;
                      }}
                      lazyImage={true}
                    />
                  </SuspenseWrapper>
                );
              })}
            </ResponsiveTechListWrapper>
          </TechnologyWrapper>
          <ApplicationsWrapper className="applications-wrapper">
            <Typography
              typeElement={TYPE_ELEMENTS.H_3}
              isOutline="true"
              customCase="uppercase"
            >
              {APPLICATIONS.HEADER}
            </Typography>
            <SuspenseWrapper>
              <HorizontalAccordionCards cards={APPLICATIONS.CONTENT} />
            </SuspenseWrapper>
          </ApplicationsWrapper>
          <BottomSectionWrapper className="bottom-section-wrapper">
            <MessageWrapper className="message-wrapper" ref={mssgRef}>
              <Typography
                typeElement={TYPE_ELEMENTS.H_3}
                isOutline="true"
                customCase="uppercase"
              >
                {MESSAGE.HEADER}
              </Typography>
              <Typography typeElement={TYPE_ELEMENTS.BODY_2} customWeight="300">
                {MESSAGE.SUBTITLE}
                <MoreInfoWrapper onClick={() => setShowFounderModal(true)}>
                  <MoreInfoElement linkText={CONSTANTS.LEARN_MORE} />
                </MoreInfoWrapper>
              </Typography>
              {showMssgVideo && (
                <>
                  <MessageVideo
                    src={MESSAGE.VIDEO}
                    poster={MESSAGE.POSTER}
                    controls
                  />
                  <MessageBg className="message-bg" src={MESSAGE.BG} />
                </>
              )}
            </MessageWrapper>
            <SuspenseWrapper>
              <Partners partners={PARTNERS} />
            </SuspenseWrapper>
            <HighlightWrapper className="highlight-wrapper">
              <HighlightBg
                className="highlight-bg"
                src={HIGHLIGHTS.BG}
                alt={HIGHLIGHTS.ALT}
              />
              <HighlightHeader className="highlight-header">
                <Typography
                  typeElement={TYPE_ELEMENTS.H_3}
                  isOutline="true"
                  customCase="uppercase"
                >
                  {HIGHLIGHTS.HEADER}
                </Typography>
              </HighlightHeader>
              <HighlightList className="highlight-list" ref={highlightListRef}>
                {HIGHLIGHTS.CONTENT.map((highlight, i) => {
                  const { IMG, TEXTS, DATE, LINK, ALT } = highlight;

                  if (i === 2 && deviceType === "phone") return;

                  return (
                    <SuspenseWrapper key={i}>
                      <LinkCard
                        key={i}
                        img={IMG}
                        alt={ALT}
                        texts={TEXTS}
                        linkText={CONSTANTS.READ_MORE}
                        date={DATE}
                        link={LINK + "/" + TEXTS[0].replaceAll(" ", "-")}
                      />
                    </SuspenseWrapper>
                  );
                })}
              </HighlightList>
            </HighlightWrapper>
          </BottomSectionWrapper>
        </NoPaddingPageContentWrapper>
        {showFounderModal && (
          <SuspenseWrapper>
            <ProfileModal
              img={FOUNDER.IMG}
              links={FOUNDER.SOCIALS}
              details={FOUNDER.DESCRIPTION}
              name={FOUNDER.NAME}
              designation={FOUNDER.DESIGNATION}
              onClose={() => setShowFounderModal(false)}
            />
          </SuspenseWrapper>
        )}
      </HomeWrapper>
    </>
  );
}
